import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import DialogContent from '@material-ui/core/DialogContent';
import LoadingOverlay from '@configuratorware/configurator-frontendgui/Framework/Components/LoadingOverlay';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import { TextField } from '@material-ui/core';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(
  (theme) => {
    return {
      dialogStyle: {
        height: 'calc(100% - 150px)',
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          maxWidth: '390px',
        },
        [theme.breakpoints.down('xs')]: {
          height: 'calc(100% - 64px)',
        },
        [theme.breakpoints.between('sm', 'xl')]: {
          width: '470px',
          maxWidth: '470px',
        },
        [theme.breakpoints.up('md')]: {
          marginLeft: '120px',
          marginTop: 120,
          '& .MuiDialog-scrollPaper': {
            alignItems: 'flex-start',
          },
        },
        '& .MuiDialog-paperScrollPaper': {
          borderColor: 'transparent',
        },
      },
      [theme.breakpoints.between('sm', 'md')]: {
        [theme.orientation.portrait]: {
          top: '-110px !important',
        },
      },
      '& > div.MuiDialog-container': {
        [theme.breakpoints.between('sm', 'lg')]: {
          [theme.orientation.portrait]: {
            alignItems: 'flex-end',
          },
          [theme.orientation.landscape]: {
            alignItems: 'flex-start',
            marginTop: 35,
          },
        },
      },
      title: {
        fontWeight: 'bold',
        maxWidth: 300,
      },
      component: {
        fontWeight: 'bold',
        borderColor: theme.palette.primary.darkText,
        marginRight: 7,
        marginBottom: 8,
        '&.MuiButton-root': {
          minWidth: '150px',
        },
      },
      selectedComponent: {
        backgroundColor: theme.palette.primary.darkText,
        color: '#ffffff',
        '&:hover': {
          backgroundColor: theme.palette.primary.darkText,
        },
      },
      backDrop: {
        backgroundColor: 'rgba(0,0,0,0.0)',
      },
      subOptionContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginRight: 10,
        maxHeight: 32,
      },
      checkContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: '0px 15px 0px 8px',
        justifyContent: 'space-between',
      },
      subOptionTitle: {
        display: 'flex',
        alignItems: 'center',
      },
      divider: {
        backgroundColor: '#a8a8a8',
        height: 1,
        width: 'calc(100% - 20px)',
        margin: '0px 10px 10px',
      },
      selectedSubOptions: {
        backgroundColor: '#d8d8d8',
        marginBottom: 8,
        paddingBottom: 4,
      },
      radioRoot: {
        padding: 4,
      },
      checkedOption: {
        color: '#000000 !important',
      },
      selectRoot: {
        width: '100%',
        marginBottom: 10,
      },
      textField: {
        marginBottom: 15,
      },
    };
  },
  {
    name: 'OptionsList',
  }
);

const OptionsList = ({
  addCheckedOptionclassification,
  children,
  isLoading,
  onClose,
  onSelect,
  onToggle,
  optionclassification,
  options,
  show,
  viewDetails,
  viewStockInformation,
  LoadingComponent,
  OptionComponent,
  isAdminMode,
  groupedComponents,
  selectedComponent,
  selectComponent,
  selectedProductPart,
  selectedParentOptionsIdentifier,
  saveSubOptionMappingToCustomData,
  saveChosenColor,
  deselectMultipleOptions,
}) => {
  const classes = useStyles();

  const isSelected = (option, selected) => {
    return selected
      ? option.subOptions && option.subOptions.length > 0
        ? selectedParentOptionsIdentifier.find((oi) => oi === option.identifier)
        : selected
      : false;
  };

  const [isColorOfChoiceSelected, setIsColorOfChoiceSelected] = useState(false);

  const customColor = useSelector((state) =>
    get(state, 'configurator.configuration.customdata.aussenlackierung', '')
  );

  const handleColorInput = (event) => {
    saveChosenColor(event.target.value);
  };

  useEffect(() => {
    options.map((option) => {
      if (option.identifier === 'ral_custom_color') {
        setIsColorOfChoiceSelected(option.amount > 0 && optionclassification.identifier === 'aussenlackierung');
      }
    });
  }, [options]);

  const createOptions = () => {
    const onOptionClicked = () => {
      addCheckedOptionclassification(optionclassification);
    };
    const currentOptions = options;
    return currentOptions.map((option) => {
      let optionIndex = -1;

      const optionGroupChildren = get(option, 'option_group.children', []);

      if (optionGroupChildren.length === 0) {
        optionIndex = findIndex(get(optionclassification, 'selectedoptions'), {
          identifier: option.identifier,
        });
      } else {
        for (const item of optionGroupChildren) {
          optionIndex = findIndex(get(optionclassification, 'selectedoptions'), {
            identifier: item.identifier,
          });
          if (optionIndex > -1) {
            // we need the first selected option
            break;
          }
        }
      }

      const selected = optionIndex <= -1 ? false : !(option.amount_is_selectable && option.amount < 1);
      const alignmentAttribute = get(optionclassification, 'attributes', []).find(
        (attr) => attr.identifier === 'alignment'
      );
      const isPrioritized = alignmentAttribute ? alignmentAttribute.values[0].value === 'exterieur' : false;
      const isDisabled = !isPrioritized && get(option, 'check_results.status', true) !== true;

      return (
        <div
          className={clsx(isSelected(option, selected) && classes.selectedSubOptions)}
          key={option.identifier}
        >
          <OptionComponent
            key={'optionlistitem' + option.identifier}
            option={option}
            optionclassification={optionclassification}
            onSelect={
              isDisabled
                ? () => {}
                : option.subOptions && option.subOptions.length > 0
                ? (option) => {
                    if (option.amount) {
                      saveSubOptionMappingToCustomData({
                        optionclassificationIdentifier: get(optionclassification, 'identifier'),
                        optionIdentifier: get(option, 'identifier'),
                        subOption: get(option, 'subOptions[0]'),
                        amount: 1,
                      });
                      onSelect({ ...option.subOptions[0], amount: option.amount });
                    }
                  }
                : (option) => onSelect(option)
            }
            selected={isSelected(option, selected)}
            viewDetails={viewDetails}
            viewStockInformation={viewStockInformation}
            onClick={onOptionClicked}
            onToggle={onToggle}
            isAdminMode={isAdminMode}
            isDisabled={isDisabled}
            className={isDisabled ? 'disabled' : ''}
          />
          {selectedParentOptionsIdentifier.find((so) => so === option.identifier) &&
            option.subOptions &&
            option.subOptions.length > 0 && (
              <>
                <div className={classes.divider}></div>
                <RadioGroup
                  value={get(
                    option.subOptions.find(
                      (o) => option.selectedSubOption && o.identifier === option.selectedSubOption.identifier
                    ),
                    'identifier'
                  )}
                  onChange={async (e) => {
                    if (
                      option.selectedSubOption &&
                      option.selectedSubOption.identifier !== option.identifier &&
                      optionclassification.selectedoptions.find(
                        (o) => o.identifier === option.selectedSubOption.identifier
                      )
                    ) {
                      await onSelect({ ...option.selectedSubOption, amount: 0 });
                    }
                    onSelect({
                      ...option.subOptions.find((o) => o.identifier === e.target.value),
                      amount: 1,
                    });
                  }}
                >
                  {option.subOptions.map((subOption, i) => (
                    <div className={classes.checkContainer} key={i}>
                      <div className={classes.subOptionTitle}>
                        <Radio
                          color="primary"
                          onChange={() => {
                            saveSubOptionMappingToCustomData({
                              optionclassificationIdentifier: get(optionclassification, 'identifier'),
                              optionIdentifier: get(option, 'identifier'),
                              subOption: subOption,
                              amount: 1,
                            });
                            onSelect({ ...subOption, amount: 1 });
                          }}
                          disabled={!isPrioritized && get(subOption, 'check_results.status', true) !== true}
                          value={subOption.identifier}
                          classes={{ root: classes.radioRoot, checked: classes.checkedOption }}
                        />
                        <Typography className={classes.title} component="div" variant="body2">
                          {subOption.title}
                        </Typography>
                      </div>
                      <Typography variant="body2" component="div">
                        {subOption.priceFormatted}
                      </Typography>
                    </div>
                  ))}
                </RadioGroup>
              </>
            )}
        </div>
      );
    });
  };

  const renderOptions = () => {
    const optionListItems = createOptions();

    return (
      <div>
        {children ? children : optionListItems.length > 0 ? optionListItems : null}
        <LoadingComponent show={isLoading} />
      </div>
    );
  };

  return (
    <CustomDialog
      className={clsx(classes.dialogStyle, customClassName('options'))}
      dialogTitle={get(selectedProductPart, 'title', '')}
      onCancel={onClose}
      onClose={onClose}
      open={show && !!optionclassification.identifier}
      BackdropProps={{
        classes: {
          root: classes.backDrop,
        },
      }}
    >
      <DialogContent className={classes.contentStyle}>
        {groupedComponents.map((component) => {
          const alignmentAttribute = get(component, 'attributes', []).find(
            (attr) => attr.identifier === 'alignment'
          );
          const alignmentAttributeTitle = alignmentAttribute
            ? alignmentAttribute.values[0].translated_value
            : component.title;
          return (
            <Button
              className={clsx(
                classes.component,
                selectedComponent.identifier === component.identifier && classes.selectedComponent
              )}
              key={component.identifier}
              variant={selectedComponent.identifier === component.identifier ? 'contained' : 'outlined'}
              onClick={() => selectComponent(get(component, 'identifier', null))}
            >
              {alignmentAttributeTitle}
            </Button>
          );
        })}
        {renderOptions()}
        {isColorOfChoiceSelected && (
          <>
            <TextField
              value={customColor}
              onChange={handleColorInput}
              fullWidth
              label={t('colorOfChoice')}
            />
            <Typography className={classes.textField}>{t('colorOfChoiceText')}</Typography>
          </>
        )}
      </DialogContent>
    </CustomDialog>
  );
};

OptionsList.defaultProps = {
  addCheckedOptionclassification: () => {},
  isLoading: false,
  onClose: () => {},
  onSelect: () => {},
  onToggle: () => {},
  options: [],
  optionclassification: {},
  show: false,
  viewDetails: () => {},
  viewStockInformation: () => {},
  LoadingComponent: LoadingOverlay,
  OptionComponent: Option,
};

OptionsList.propTypes = {
  addCheckedOptionclassification: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  options: PropTypes.array,
  optionclassification: PropTypes.object,
  show: PropTypes.bool,
  viewDetails: PropTypes.func,
  viewStockInformation: PropTypes.func,
  LoadingComponent: PropTypes.elementType,
  OptionComponent: PropTypes.elementType,
  isAdminMode: PropTypes.bool,
  groupedComponents: PropTypes.array,
  selectedComponent: PropTypes.object,
  selectedProductPart: PropTypes.object,
  selectComponent: PropTypes.func,
  selectedSubOptions: PropTypes.array,
  selectedParentOptionsIdentifier: PropTypes.array,
  saveSubOptionMappingToCustomData: PropTypes.func,
  saveChosenColor: PropTypes.func,
  deselectMultipleOptions: PropTypes.func,
};

export default OptionsList;
